// DeviceComparisonReport.js
// Read-only report comparing which devices AssetPro believes are installed on
// each asset against the IoT-Ops integration registry, and surfacing the
// differences for manual reconciliation. Compare-and-display only: no writes,
// no sync.
//
// Data sources:
//   - AssetPro assets+devices: GET /asset/asset-all (via apiGet; one fresh call,
//     paginated). Devices are child asset records grouped under their host.
//   - IoT-Ops registry: GET /ops-api/integration/devices (via fetchOpsDevices),
//     authenticated with a runtime iotint_... key.
//
// Matching is EXACT on normalized identifiers (see utils/deviceComparison.js).
// Nothing is fuzzy-matched, fabricated, or silently excluded.

import React, { useState, useMemo, useCallback } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Icon,
  Input,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import moment from 'moment';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { apiGet } from './utils/apiService';
import { getUserData } from './utils/authService';
import { fetchOpsDevices, getOpsKey, setOpsKey } from './utils/opsIntegrationService';
import {
  buildAssetProSide,
  buildOpsSide,
  compareSides,
  isSoldLocation,
  toExportRows,
  CATEGORY,
  CATEGORY_LABEL,
  CATEGORY_COLOR,
  DEVICE_KIND_LABEL
} from './utils/deviceComparison';

const { Option } = Select;

const ASSET_PAGE_SIZE = 5000;
const ASSET_MAX_PAGES = 20; // safety cap (100k assets)

// Extract the assets array from the /asset/asset-all envelope across shapes.
const extractAssets = (res) => {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.assets)) return d.assets;
  if (d && Array.isArray(d.results)) return d.results;
  if (d && Array.isArray(d.docs)) return d.docs;
  if (d && Array.isArray(d.rows)) return d.rows;
  return [];
};

// Fetch all AssetPro assets (fresh, paginated). Throws on a hard failure so the
// caller can show a clear error rather than compare against partial data.
const fetchAllAssets = async () => {
  const assets = [];
  let truncated = false;
  for (let p = 0; p < ASSET_MAX_PAGES; p += 1) {
    const offset = p * ASSET_PAGE_SIZE;
    // eslint-disable-next-line no-await-in-loop
    const res = await apiGet(`/asset/asset-all?limit=${ASSET_PAGE_SIZE}&offset=${offset}`, {
      headers: { Accept: '*/*' }
    });
    if (res && res.success === false) {
      if (p === 0) throw new Error(res.error || 'Failed to load AssetPro assets');
      // Non-first-page failure: stop and report rather than silently truncate.
      throw new Error(`Failed while paginating AssetPro assets (page ${p + 1}): ${res.error}`);
    }
    const page = extractAssets(res);
    assets.push(...page);
    if (page.length < ASSET_PAGE_SIZE) break;
    if (p === ASSET_MAX_PAGES - 1) truncated = true;
  }
  return { assets, truncated };
};

// Fetch the AssetPro /location list and build (a) an id -> name map and (b) the
// set of location ids whose name is "SOLD". Needed because /asset/asset-all sends
// only the location id, not its name. Returns null-ish results on failure so the
// caller can warn and still compare (SOLD hiding just won't apply).
const fetchLocations = async () => {
  const res = await apiGet('/location', { headers: { Accept: '*/*' } });
  if (res && res.success === false) {
    return { ok: false, error: res.error || 'Failed to load locations', byId: new Map(), soldIds: new Set() };
  }
  const list = extractAssets(res); // same envelope shapes as assets
  const byId = new Map();
  const soldIds = new Set();
  list.forEach((loc) => {
    const id = loc && (loc._id || loc.id);
    if (!id) return;
    const name = loc.name || loc.code || null;
    byId.set(String(id), name);
    if (isSoldLocation(name)) soldIds.add(String(id));
  });
  return { ok: true, error: null, byId, soldIds, count: list.length };
};

// The AssetPro organisation the user is logged into AIM as. The IoT-Ops side is
// scoped by the integration TOKEN (not a query param), so we can only verify the
// two orgs line up — we surface both and warn on divergence rather than silently
// comparing mismatched populations.
const getAimOrg = () => {
  const ud = getUserData();
  const org = ud && ud.organisation;
  if (!org) return { id: null, name: null };
  if (typeof org === 'string') return { id: null, name: org };
  return { id: org._id || org.id || null, name: org.name || org.slug || null };
};

// Loose textual comparison used ONLY to decide whether to show an amber "please
// verify" note. It never drops, alters, or auto-excludes any data (that would be
// fuzzy matching of business records) — it just prompts a human check.
const looksLikeSameOrg = (aimName, opsName) => {
  if (!aimName || !opsName) return null; // unknown -> can't judge, prompt review
  const a = String(aimName).trim().toLowerCase();
  const b = String(opsName).trim().toLowerCase();
  if (!a || !b) return null;
  return a === b || a.includes(b) || b.includes(a);
};

const statusTag = (status) => {
  const map = {
    matched: { color: 'green', label: 'Matched' },
    only_assetpro: { color: 'blue', label: 'Only AssetPro' },
    only_ops: { color: 'purple', label: 'Only IoT-Ops' },
    only_ops_info: { color: 'default', label: 'IoT-Ops (info)' },
    review: { color: 'red', label: 'Review' }
  };
  const m = map[status] || { color: 'default', label: status };
  return <Tag color={m.color}>{m.label}</Tag>;
};

// Organisation-scope banner: confirms the IoT-Ops token org lines up with the
// AIM login org, or warns when it doesn't (the wrong-org-token footgun).
const renderScopeBanner = (scope) => {
  if (!scope) return null;
  const aim = scope.aimOrg || {};
  const ops = scope.opsClients || [];
  const opsLabel = ops.length ? ops.map((c) => c.name || c.id || 'unknown').join(', ') : 'unknown';
  const pair = (
    <span>
      AIM org: <strong>{aim.name || 'unknown'}</strong> · IoT-Ops token org: <strong>{opsLabel}</strong>
    </span>
  );

  if (scope.status === 'match') {
    return (
      <Alert type="success" showIcon style={{ marginBottom: 16 }}
        message="Organisation scope verified" description={pair} />
    );
  }
  if (scope.status === 'mismatch') {
    return (
      <Alert type="error" showIcon style={{ marginBottom: 16 }}
        message="Organisation mismatch — the IoT-Ops token is for a different organisation"
        description={<span>{pair}<br />Every asset will appear mismatched. Paste an integration key issued for the organisation you are logged into AIM as.</span>} />
    );
  }
  if (scope.status === 'multi') {
    return (
      <Alert type="error" showIcon style={{ marginBottom: 16 }}
        message="IoT-Ops returned more than one organisation"
        description={<span>{pair}<br />An integration token should map to a single organisation. Verify the token.</span>} />
    );
  }
  if (scope.status === 'unverified') {
    return (
      <Alert type="warning" showIcon style={{ marginBottom: 16 }}
        message="Could not confirm the organisations match — please verify"
        description={<span>{pair}<br />The comparison assumes both are the same organisation. If they are not, results will be misleading.</span>} />
    );
  }
  return (
    <Alert type="warning" showIcon style={{ marginBottom: 16 }}
      message="Organisation scope could not be verified"
      description={<span>The IoT-Ops response carried no organisation identity. Confirm the token belongs to your AIM organisation (<strong>{aim.name || 'unknown'}</strong>).</span>} />
  );
};

const CATEGORY_ORDER = [
  CATEGORY.NEEDS_REVIEW,
  CATEGORY.DEVICE_MISMATCH,
  CATEGORY.ONLY_ASSETPRO,
  CATEGORY.ONLY_OPS,
  CATEGORY.MATCH
];

function DeviceComparisonReport() {
  const [opsKeyInput, setOpsKeyInput] = useState(getOpsKey());
  const [keySaved, setKeySaved] = useState(Boolean(getOpsKey()));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [result, setResult] = useState(null); // { rows, summary }
  const [meta, setMeta] = useState(null); // fetch diagnostics
  const [ranAt, setRanAt] = useState(null);

  const [textFilter, setTextFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  // AssetPro assets with no devices on either side can never match the Ops
  // registry (which only lists installed devices), so they are hidden by default.
  const [hideDeviceless, setHideDeviceless] = useState(true);
  // Assets sold in AssetPro (SOLD location) that IoT-Ops no longer lists are
  // expected, not conflicts — hidden by default. (A sold asset still present in
  // IoT-Ops IS a conflict and is always shown.)
  const [hideSold, setHideSold] = useState(true);

  const handleSaveKey = useCallback(() => {
    const trimmed = String(opsKeyInput || '').trim();
    if (!trimmed) {
      message.warning('Paste an IoT-Ops integration key (iotint_...) first.');
      return;
    }
    setOpsKey(trimmed);
    setKeySaved(true);
    message.success('IoT-Ops integration key saved.');
  }, [opsKeyInput]);

  const handleCompare = useCallback(async () => {
    const key = String(opsKeyInput || '').trim();
    if (!key) {
      message.error('Paste and save an IoT-Ops integration key first.');
      return;
    }
    // Persist whatever is in the field so the fetch uses the current value.
    setOpsKey(key);
    setKeySaved(true);

    setLoading(true);
    setError(null);
    setWarnings([]);
    setResult(null);
    setMeta(null);

    try {
      // All sources fetched fresh, in parallel. AssetPro throws on hard failure;
      // Ops returns success:false with a message; locations returns ok:false.
      const [assetOutcome, opsOutcome, locOutcome] = await Promise.all([
        fetchAllAssets(),
        fetchOpsDevices(),
        fetchLocations()
      ]);

      if (!opsOutcome.success) {
        throw new Error(opsOutcome.error || 'Failed to fetch IoT-Ops devices');
      }

      const apResult = buildAssetProSide(assetOutcome.assets);
      const opsResult = buildOpsSide(opsOutcome.rows);
      // Resolve SOLD by exact location id (asset-all sends only the location id).
      const compared = compareSides(apResult, opsResult, {
        soldLocationIds: locOutcome.soldIds,
        locationsById: locOutcome.byId
      });

      const nextWarnings = [];
      if (assetOutcome.truncated) {
        nextWarnings.push(
          `AssetPro asset list hit the ${ASSET_MAX_PAGES}-page safety cap — results may be incomplete.`
        );
      }
      if (opsOutcome.meta && opsOutcome.meta.truncated) {
        nextWarnings.push('IoT-Ops device list hit the page safety cap — results may be incomplete.');
      }
      if (apResult.orphans.length > 0) {
        nextWarnings.push(
          `${apResult.orphans.length} AssetPro device record(s) could not be linked to a host asset (excluded from per-asset rows).`
        );
      }
      if (opsResult.stats.rowsWithoutCode > 0) {
        nextWarnings.push(
          `${opsResult.stats.rowsWithoutCode} IoT-Ops row(s) had no assetCode and were excluded.`
        );
      }
      if (!locOutcome.ok) {
        nextWarnings.push(
          `Could not load the AssetPro location list (${locOutcome.error}) — sold assets cannot be identified, so they are NOT hidden.`
        );
      } else if (locOutcome.soldIds.size === 0) {
        nextWarnings.push(
          'No AssetPro location named "SOLD" was found — sold assets cannot be identified, so they are NOT hidden.'
        );
      }

      // Organisation-scope check. The IoT-Ops side is scoped by the integration
      // TOKEN, so a token for the wrong org silently compares mismatched
      // populations. We verify the token's org against the AIM login org and flag
      // divergence loudly (never auto-dropping data).
      const aimOrg = getAimOrg();
      const opsClients = opsResult.stats.clients || [];
      let scopeStatus = 'unknown';
      if (opsClients.length > 1) {
        scopeStatus = 'multi';
      } else if (opsClients.length === 1) {
        const same = looksLikeSameOrg(aimOrg.name, opsClients[0].name);
        scopeStatus = same === true ? 'match' : same === false ? 'mismatch' : 'unverified';
      }
      const scope = { aimOrg, opsClients, status: scopeStatus };

      setResult(compared);
      setMeta({
        assetPro: apResult.stats,
        ops: opsOutcome.meta,
        opsStats: opsResult.stats,
        scope
      });
      setWarnings(nextWarnings);
      setRanAt(moment());

      if (compared.rows.length === 0) {
        message.info('No assets found on either side to compare.');
      }
    } catch (err) {
      setError(err.message || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  }, [opsKeyInput]);

  const allRows = result ? result.rows : [];
  const hiddenDevicelessCount = useMemo(
    () => allRows.filter((r) => r.deviceless).length,
    [allRows]
  );
  // Sold in AssetPro AND absent from IoT-Ops => expected, not a conflict.
  const hiddenSoldCount = useMemo(
    () => allRows.filter((r) => r.soldHidden).length,
    [allRows]
  );

  // Rows in scope for the current view (device-less + sold-orphan hidden by default).
  const scopedRows = useMemo(
    () => allRows.filter((r) => {
      if (hideDeviceless && r.deviceless) return false;
      if (hideSold && r.soldHidden) return false;
      return true;
    }),
    [allRows, hideDeviceless, hideSold]
  );

  // Category counts for the visible scope (so summary + dropdown match the table).
  const summaryCounts = useMemo(() => {
    const counts = {
      totalAssets: scopedRows.length,
      [CATEGORY.MATCH]: 0,
      [CATEGORY.DEVICE_MISMATCH]: 0,
      [CATEGORY.ONLY_ASSETPRO]: 0,
      [CATEGORY.ONLY_OPS]: 0,
      [CATEGORY.NEEDS_REVIEW]: 0
    };
    scopedRows.forEach((r) => { counts[r.category] += 1; });
    return counts;
  }, [scopedRows]);

  const filteredRows = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    return scopedRows.filter((r) => {
      if (categoryFilter !== 'ALL' && r.category !== categoryFilter) return false;
      if (!q) return true;
      if (String(r.assetCode || '').toLowerCase().includes(q)) return true;
      if (String(r.opsAssetCode || '').toLowerCase().includes(q)) return true;
      // Match on any device id (either side).
      return r.devices.some(
        (d) =>
          String(d.assetProId || '').toLowerCase().includes(q) ||
          String(d.opsId || '').toLowerCase().includes(q)
      );
    });
  }, [scopedRows, textFilter, categoryFilter]);

  const handleExportCsv = useCallback(() => {
    if (!result) return;
    const rows = toExportRows(filteredRows);
    if (rows.length === 0) {
      message.warning('Nothing to export for the current filter.');
      return;
    }
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Device_Comparison_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('CSV downloaded');
  }, [result, filteredRows]);

  const handleExportXlsx = useCallback(() => {
    if (!result) return;
    const rows = toExportRows(filteredRows);
    if (rows.length === 0) {
      message.warning('Nothing to export for the current filter.');
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Device Comparison');
    XLSX.writeFile(workbook, `Device_Comparison_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`);
    message.success('XLSX downloaded');
  }, [result, filteredRows]);

  const columns = [
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      width: 180,
      render: (code, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {code}
            {r.soldConflict && <Tag color="red" style={{ marginLeft: 6 }}>SOLD but in IoT-Ops</Tag>}
          </div>
          {r.assetCodesDiffer && (
            <div style={{ fontSize: 11, color: '#d46b08' }}>
              IoT-Ops: {r.opsAssetCode} <span style={{ color: '#bfbfbf' }}>(prefix differs)</span>
            </div>
          )}
          {(r.altName || r.ownerCode) && (
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {r.altName || ''}{r.altName && r.ownerCode ? ' · ' : ''}{r.ownerCode ? `Owner ${r.ownerCode}` : ''}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 160,
      render: (cat) => <Tag color={CATEGORY_COLOR[cat]}>{CATEGORY_LABEL[cat]}</Tag>
    },
    {
      title: 'AssetPro devices',
      dataIndex: 'apDeviceCount',
      key: 'apDeviceCount',
      width: 130,
      align: 'center'
    },
    {
      title: 'IoT-Ops devices',
      dataIndex: 'opsDeviceCount',
      key: 'opsDeviceCount',
      width: 130,
      align: 'center'
    },
    {
      title: 'Matched',
      dataIndex: 'matched',
      key: 'matched',
      width: 90,
      align: 'center',
      render: (v) => (v > 0 ? <Tag color="green">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>)
    },
    {
      title: 'Differences',
      key: 'diffs',
      width: 200,
      render: (_, r) => (
        <span>
          {r.apOnly > 0 && <Tag color="blue">{r.apOnly} AssetPro-only</Tag>}
          {r.opsOnly > 0 && <Tag color="purple">{r.opsOnly} Ops-only</Tag>}
          {r.opsInfo > 0 && <Tag>{r.opsInfo} SIM/info</Tag>}
          {r.apOnly === 0 && r.opsOnly === 0 && r.opsInfo === 0 && <span style={{ color: '#bfbfbf' }}>—</span>}
        </span>
      )
    },
    {
      title: 'Review notes',
      key: 'review',
      render: (_, r) =>
        r.needsReviewReasons.length > 0 ? (
          <Tooltip title={r.needsReviewReasons.join(' · ')}>
            <span style={{ color: '#cf1322' }}>
              <Icon type="warning" /> {r.needsReviewReasons.length} issue{r.needsReviewReasons.length > 1 ? 's' : ''}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: '#bfbfbf' }}>—</span>
        )
    }
  ];

  // Per-asset expanded detail: one row per device identifier.
  const expandedRowRender = (record) => {
    const deviceColumns = [
      { title: 'Device', dataIndex: 'kind', key: 'kind', width: 130, render: (k) => DEVICE_KIND_LABEL[k] || k },
      { title: 'Status', dataIndex: 'status', key: 'status', width: 130, render: statusTag },
      { title: 'AssetPro ID', dataIndex: 'assetProId', key: 'assetProId', render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
      { title: 'IoT-Ops ID', dataIndex: 'opsId', key: 'opsId', render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
      { title: 'IoT-Ops role', dataIndex: 'role', key: 'role', width: 140, render: (v) => v || '-' },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        render: (notes) =>
          notes && notes.length > 0 ? (
            <span style={{ color: '#d46b08', fontSize: 12 }}>{notes.join('; ')}</span>
          ) : (
            <span style={{ color: '#bfbfbf' }}>—</span>
          )
      }
    ];
    if (record.devices.length === 0) {
      return <div style={{ color: '#8c8c8c', padding: '8px 0' }}>No devices recorded on either side for this asset.</div>;
    }
    return (
      <Table
        columns={deviceColumns}
        dataSource={record.devices}
        pagination={false}
        size="small"
        rowKey="key"
      />
    );
  };

  return (
    <div className="device-comparison-report">
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <span>
            <Icon type="deployment-unit" /> AssetPro ↔ IoT-Ops Device Comparison
          </span>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Read-only comparison"
          description="Fetches AssetPro assets/devices and the IoT-Ops installed-device registry, compares them by exact identifier, and shows the differences. It never writes or syncs. Paste your IoT-Ops integration key (iotint_...) below — it is stored only in this browser."
        />

        <Row gutter={12} type="flex" align="middle">
          <Col xs={24} md={14} style={{ marginBottom: 8 }}>
            <Input.Password
              placeholder="IoT-Ops integration key (iotint_...)"
              value={opsKeyInput}
              onChange={(e) => { setOpsKeyInput(e.target.value); setKeySaved(false); }}
              addonBefore={<Icon type="key" />}
              autoComplete="off"
            />
          </Col>
          <Col xs={12} md={4} style={{ marginBottom: 8 }}>
            <Button block onClick={handleSaveKey} icon="save">
              Save key {keySaved && <Icon type="check" style={{ color: '#52c41a' }} />}
            </Button>
          </Col>
          <Col xs={12} md={6} style={{ marginBottom: 8 }}>
            <Button block type="primary" icon="sync" loading={loading} onClick={handleCompare}>
              Compare
            </Button>
          </Col>
        </Row>

        {ranAt && !loading && (
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            Last compared {ranAt.format('YYYY-MM-DD HH:mm:ss')}
            {meta && meta.ops && ` · IoT-Ops: ${meta.ops.fetched} device rows`}
            {meta && meta.assetPro && ` · AssetPro: ${meta.assetPro.assets} assets, ${meta.assetPro.deviceRecords} device records`}
          </div>
        )}
      </Card>

      {error && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          message="Comparison failed"
          description={error}
          onClose={() => setError(null)}
        />
      )}

      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Data completeness warnings"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        />
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" tip="Fetching AssetPro assets and IoT-Ops devices…" />
        </div>
      )}

      {!loading && meta && meta.scope && renderScopeBanner(meta.scope)}

      {!loading && result && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Assets compared" value={summaryCounts.totalAssets} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title={CATEGORY_LABEL[CATEGORY.MATCH]} value={summaryCounts[CATEGORY.MATCH]} valueStyle={{ color: '#52c41a' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title={CATEGORY_LABEL[CATEGORY.DEVICE_MISMATCH]} value={summaryCounts[CATEGORY.DEVICE_MISMATCH]} valueStyle={{ color: '#fa8c16' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title={CATEGORY_LABEL[CATEGORY.ONLY_ASSETPRO]} value={summaryCounts[CATEGORY.ONLY_ASSETPRO]} valueStyle={{ color: '#1890ff' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title={CATEGORY_LABEL[CATEGORY.ONLY_OPS]} value={summaryCounts[CATEGORY.ONLY_OPS]} valueStyle={{ color: '#722ed1' }} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic title={CATEGORY_LABEL[CATEGORY.NEEDS_REVIEW]} value={summaryCounts[CATEGORY.NEEDS_REVIEW]} valueStyle={{ color: '#cf1322' }} />
            </Col>
          </Row>
        </Card>
      )}

      {!loading && result && (
        <Card size="small">
          <Row gutter={12} type="flex" align="middle" style={{ marginBottom: 12 }}>
            <Col xs={24} md={10} style={{ marginBottom: 8 }}>
              <Input
                allowClear
                prefix={<Icon type="search" />}
                placeholder="Filter by asset code or device id…"
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
              />
            </Col>
            <Col xs={12} md={7} style={{ marginBottom: 8 }}>
              <Select style={{ width: '100%' }} value={categoryFilter} onChange={setCategoryFilter}>
                <Option value="ALL">All categories ({scopedRows.length})</Option>
                {CATEGORY_ORDER.map((c) => (
                  <Option key={c} value={c}>
                    {CATEGORY_LABEL[c]} ({summaryCounts[c]})
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={7} style={{ marginBottom: 8, textAlign: 'right' }}>
              <Button icon="download" onClick={handleExportCsv} style={{ marginRight: 8 }} disabled={filteredRows.length === 0}>
                CSV
              </Button>
              <Button icon="file-excel" onClick={handleExportXlsx} disabled={filteredRows.length === 0}>
                XLSX
              </Button>
            </Col>
          </Row>

          <div style={{ marginBottom: 12 }}>
            <Checkbox
              checked={hideDeviceless}
              onChange={(e) => setHideDeviceless(e.target.checked)}
            >
              Hide AssetPro assets with no devices
              {hiddenDevicelessCount > 0 && (
                <span style={{ color: '#8c8c8c' }}> ({hiddenDevicelessCount} {hideDeviceless ? 'hidden' : 'shown'})</span>
              )}
            </Checkbox>
            <Checkbox
              checked={hideSold}
              onChange={(e) => setHideSold(e.target.checked)}
              style={{ marginLeft: 16 }}
            >
              Hide sold assets not in IoT-Ops
              {hiddenSoldCount > 0 && (
                <span style={{ color: '#8c8c8c' }}> ({hiddenSoldCount} {hideSold ? 'hidden' : 'shown'})</span>
              )}
            </Checkbox>
          </div>

          <Table
            columns={columns}
            dataSource={filteredRows}
            rowKey="key"
            size="small"
            expandedRowRender={expandedRowRender}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} assets` }}
            scroll={{ x: 1100 }}
          />
        </Card>
      )}
    </div>
  );
}

export default DeviceComparisonReport;
